import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { secureGet, secureDelete, securePost } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { PrimaryButton } from '../../components/PrimaryButton';
import Table from '../../components/Table';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import DeletePopup from '../../components/DeletePopup';
import PageScroller from '../../components/PageScroller';
import TableLoadingArea from '../../components/TableLoadingArea';
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from '../../Shared/UserContext';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import BlockIcon from '../../icons/block.svg';
import DeleteIcon from '../../icons/trashR.svg';
import LocatedIcon from '../../icons/located.svg';
import NotLocatedIcon from '../../icons/notlocated.svg';

const Clients = () => {
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ code: "", cityName: "", located: "", blocked: "" });
  const [cities, setCities] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { state: { role } } = useContext(UserContext);

  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const topRef = useRef(null);

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const calculateCompletionPercentage = (client) => {
    const WEIGHTS = {
      ADDRESS_PERCENT: 5,
      PATENT_PERCENT: 15,
      ID_FISCAL_PERCENT: 5,
      ICE_PERCENT: 20,
      MAIN_CONTACT_NAME_PERCENT: 5,
      MAIN_CONTACT_PHONE_PERCENT: 20,
      LOCATION_PERCENT: 30,
    };

    let percentage = 0;
    if (client.latitude && client.longitude) percentage += WEIGHTS.LOCATION_PERCENT;
    if (client.address) percentage += WEIGHTS.ADDRESS_PERCENT;
    if (client.taxId) percentage += WEIGHTS.ID_FISCAL_PERCENT;

    if (client.fields) {
      client.fields.forEach(field => {
        if (field.name === "Patente" && field.value) percentage += WEIGHTS.PATENT_PERCENT;
        if (field.name === "ICE" && field.value) percentage += WEIGHTS.ICE_PERCENT;
        if (field.name === "Nom/Prénom du contact principal" && field.value) percentage += WEIGHTS.MAIN_CONTACT_NAME_PERCENT;
        if (field.name === "Numéro de téléphone" && field.value) percentage += WEIGHTS.MAIN_CONTACT_PHONE_PERCENT;
      });
    }
    return percentage;
  };

const getOptions = (item) => {
  const options = [
    { label: t(`actions.modify`), icon: EditIcon, action: () => handleModify(item) },
  ];

  if (role !== "ROLE_COMMERCIAL") {
    options.push({
      label: item.status === "BLOCKED" ? t(`actions.unblock`) : t(`actions.block`),
      color: "#EF476F",
      icon: BlockIcon,
      action: () => handleBlock(item),
    });
  }

  return options;
};


  const columns = [
    {
      label: t(`columns.code`),
      key: "code",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <img
            src={row.latitude && row.longitude ? LocatedIcon : NotLocatedIcon}
            alt={row.latitude && row.longitude ? "Located" : "Not Located"}
            width={16}
            height={16}
          />
          <span className="text-[#6A78F8] text-sm">({calculateCompletionPercentage(row)}%)</span>
          <span>{row.code}</span>
          {row.status === "BLOCKED" && (
            <span className="text-white bg-red-500 text-xs px-2 py-1 rounded-full">B</span>
          )}
        </div>
      ),
    },
    { label: t(`columns.clientName`), key: "name" },
    { label: t(`columns.city`), key: "cityName" },
    {
      key: "options",
      render: (_, item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(item)} />
        </div>
      ),
    },
  ];

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "cityName",
      label: t(`filters.clients.city`),
      type: "dropdown",
      options: cities.map(city => ({ value: city, label: city })),
    },
    {
      name: "located",
      label: t(`filters.clients.location`),
      type: "dropdown",
      options: [
        { value: "located", label: t(`options.located`) },
        { value: "not-located", label: t(`options.notLocated`) },
      ],
    },
    {
      name: "blocked",
      label: t(`filters.clients.block`),
      type: "dropdown",
      options: [
        { value: "blocked", label: t(`options.blocked`) },
        { value: "not-blocked", label: t(`options.unblocked`) },
      ],
    },
  ];

  useEffect(() => {
    let isMounted = true;
    const fetchCities = async () => {
      try {
        const { data } = await secureGet('/cities', showSnackbar);
        if (isMounted) {
          setCities(data.map(city => city.name));
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching cities:', error);
        }
      }
    };
    fetchCities();
    return () => { isMounted = false; };
  }, []);

  const fetchClientsData = useCallback(async (customFilters = filters, page = pageNumber, size = pageSize) => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", size);
    if (customFilters.needle) params.append("needle", customFilters.needle);
    if (customFilters.cityName) params.append("city", customFilters.cityName);
    if (customFilters.blocked === "blocked") params.append("status", "BLOCKED");
    else if (customFilters.blocked === "not-blocked") params.append("status", "NORMAL");
    if (customFilters.located === "located") params.append("located", "true");
    else if (customFilters.located === "not-located") params.append("located", "false");

    try {
      const { data } = await secureGet(`/clients/paged?${params.toString()}`, showSnackbar);
      if (data) {
        setClientsData(data.content || []);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      showSnackbar(t('errors.fetchClientsFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, pageNumber, pageSize, t]);

  useEffect(() => {
    const state = location.state || {};
    const newFilters = state.filters || { code: "", cityName: "", located: "", blocked: "" };
    const page = typeof state.page === "number" ? state.page : 0;
    const size = typeof state.size === "number" ? state.size : 100;

    setFilters(newFilters);
    setPageNumber(page);
    setPageSize(size);

    fetchClientsData(newFilters, page, size);
  }, []);

  const handleFilterChange = (filterName, filterValue) => {
    const updated = { ...filters, [filterName]: filterValue };
    setFilters(updated);
    setPageNumber(0);
    fetchClientsData(updated, 0, pageSize);

    history.replace({
      pathname: location.pathname,
      state: { filters: updated, page: 0, size: pageSize },
    });
  };

  const resetFilters = () => {
    const reset = { code: "", cityName: "", located: "", blocked: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchClientsData(reset, 0, pageSize);

    history.replace({
      pathname: location.pathname,
      state: { filters: reset, page: 0, size: pageSize },
    });
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleModify = (client) => {
    history.push({
      pathname: `/clients/modify/${client.id}`,
      state: {
        initialValues: client,
        filters,
        page: pageNumber,
        size: pageSize,
      },
    });
  };

  const handleBlock = async (item) => {
    try {
      const newStatus = item.status === "BLOCKED" ? "NORMAL" : "BLOCKED";
      const payload = { clientId: item.id, status: newStatus };
      await securePost('/clients/status/update', payload, showSnackbar);

      setSnackbar({
        open: true,
        message: item.status === "BLOCKED"
          ? t(`confirmation.clientUnblock`)
          : t(`confirmation.clientBlock`),
        severity: "success",
      });
      fetchClientsData();
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: t('errors.updateStatusFailed'),
        severity: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      const deleteUrl = `/clients/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl);

      setSnackbar({
        open: true,
        message: t(`confirmation.clientDelete`),
        severity: "success",
      });
      fetchClientsData();
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbar({
        open: true,
        message: t('errors.deleteClientFailed'),
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleAddClick = () => {
    history.push('/clients/add');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPageNumber(newPage);
      fetchClientsData(filters, newPage, pageSize);

      history.replace({
        pathname: location.pathname,
        state: { filters, page: newPage, size: pageSize },
      });

      if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(0);
    fetchClientsData(filters, 0, newPageSize);

    history.replace({
      pathname: location.pathname,
      state: { filters, page: 0, size: newPageSize },
    });
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.clients")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.clients`)}</h1>
        <div className="ml-auto text-[16px] flex gap-3">
          <PrimaryButton
            label={t(`primaryButtons.add_client`)}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={clientsData}
          onRowClick={(row) => {
            history.push({
              pathname: `/clients/details/${row.id}`,
              state: { filters, page: pageNumber, size: pageSize },
            });
          }}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Clients;